import { useMemo } from "react";
import { useMe } from "./queries";
import type { Member, Owner } from "./types";

export type OwnerOption = { value: Owner; label: string };

export const BOTH_LABEL = "Ambos";

/** Names and owner choices of a household (#147): seats come from its members, never a literal
 *  name. A one-person household (`single`) has no owner choices: everything is theirs. */
export function membersView(members: Member[]) {
  const names = new Map(members.map((m) => [m.seat, m.name]));
  const label = (owner: Owner): string =>
    owner === "BOTH" ? BOTH_LABEL : (names.get(owner) ?? `Persona ${owner === "SEAT_1" ? 1 : 2}`);
  const both: OwnerOption = { value: "BOTH", label: BOTH_LABEL };
  const seats: OwnerOption[] = members.map((m) => ({ value: m.seat, label: m.name }));
  const [first, second] = seats;
  return {
    members,
    label,
    /** Fewer than two members: hide owner chips, pickers and personal/joint switches. */
    single: members.length < 2,
    /** "Ana · " before a subtitle, or "" in a one-person household (everything is theirs). */
    prefix: (owner: Owner): string => (members.length < 2 ? "" : `${label(owner)} · `),
    seats,
    /** Ambos first (filters, forms). */
    options: [both, ...seats],
    /** Seat 1 · Ambos · seat 2: the quick picker on a shared account. */
    around: first && second ? [first, both, second] : [both, ...seats],
  };
}

export type MembersView = ReturnType<typeof membersView>;

export function useMembers(): MembersView {
  const members = useMe().data?.members;
  return useMemo(() => membersView(members ?? []), [members]);
}
